import { defineComponent, computed, ref, watch, onMounted, onBeforeUnmount, createElementBlock, openBlock, normalizeStyle, normalizeClass, unref, createElementVNode, createCommentVNode, renderSlot, toDisplayString, Fragment, createTextVNode, renderList } from "vue";
import { swatchToneHex, NEUTRAL_HEX, toneForHex } from "app-theme";
import { FREE_TABLE_STATE } from "app-core";
const _hoisted_1 = ["aria-label"];
const _hoisted_2 = {
  key: 2,
  class: "tf-badges"
};
const _hoisted_3 = { class: "tf-b-icon" };
const _hoisted_4 = ["innerHTML"];
const _hoisted_5 = { class: "tf-b-num" };
const _hoisted_6 = ["innerHTML"];
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "TableFace",
  props: {
    config: { default: () => ({}) },
    state: { default: () => FREE_TABLE_STATE },
    price: { default: "" },
    seatedLabel: { default: "" }
  },
  setup(__props) {
    const props = __props;
    const BADGE_PATHS = {
      qr_requests: '<path fill-rule="evenodd" d="M3 3h8v8H3zm2.4 2.4v3.2h3.2V5.4z"/><path fill-rule="evenodd" d="M13 3h8v8h-8zm2.4 2.4v3.2h3.2V5.4z"/><path fill-rule="evenodd" d="M3 13h8v8H3zm2.4 2.4v3.2h3.2v-3.2z"/><path d="M13 13h3.6v3.6H13zM17.4 17.4H21V21h-3.6z"/>',
      kitchen_ready: '<path d="M9.4 18.9c-.42 0-.84-.16-1.16-.48L3.6 13.8a1.64 1.64 0 1 1 2.32-2.32l3.48 3.48 8.6-9.42a1.64 1.64 0 0 1 2.42 2.21l-9.76 10.7c-.3.33-.72.53-1.16.54z"/>',
      booked: '<path fill-rule="evenodd" d="M8 2a1.1 1.1 0 0 1 1.1 1.1V4h5.8v-.9a1.1 1.1 0 0 1 2.2 0V4h1.4A2.5 2.5 0 0 1 21 6.5v12a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18.5v-12A2.5 2.5 0 0 1 5.5 4h1.4v-.9A1.1 1.1 0 0 1 8 2zm3 15.7c.28.28.74.28 1.02 0l4.5-4.6a1.05 1.05 0 1 0-1.5-1.47l-3.53 3.6-1.51-1.53a1.05 1.05 0 1 0-1.5 1.48z"/>',
      deadline_warning: '<path fill-rule="evenodd" d="M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zm1.05 4.1a1.05 1.05 0 0 0-2.1 0v4.7c0 .35.17.68.46.87l3.2 2.14a1.05 1.05 0 0 0 1.17-1.75l-2.73-1.82z"/>',
      no_show: '<circle cx="9.5" cy="7.4" r="3.9"/><path d="M9.5 14.2c-3.95 0-7.1 2.55-7.1 5.7 0 .45.4.8.9.8h10.1a7.1 7.1 0 0 1 2.3-5.35 11.6 11.6 0 0 0-6.2-1.15z"/><path d="M16.5 15.5l5 5M21.5 15.5l-5 5" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" fill="none"/>',
      held: '<path fill-rule="evenodd" d="M12 2.6a4.9 4.9 0 0 0-4.9 4.9v2.6h-.35A2.35 2.35 0 0 0 4.4 12.4v6.2a2.35 2.35 0 0 0 2.35 2.35h10.5a2.35 2.35 0 0 0 2.35-2.35v-6.2a2.35 2.35 0 0 0-2.35-2.3h-.35V7.5A4.9 4.9 0 0 0 12 2.6zm2.5 7.5V7.5a2.5 2.5 0 1 0-5 0v2.6z"/>'
    };
    const USERS_SOLID = '<circle cx="9" cy="7.6" r="3.9"/><path d="M9 14.4c-4 0-7.2 2.6-7.2 5.8 0 .4.4.8.9.8h12.6c.5 0 .9-.4.9-.8 0-3.2-3.2-5.8-7.2-5.8z"/><circle cx="16.6" cy="8.3" r="3"/><path d="M16.6 13.6c-.7 0-1.4.1-2 .3 1.7 1.3 2.8 3.1 2.8 5.1 0 .7-.2 1.4-.7 2h4.6c.5 0 .9-.4.9-.8 0-3.7-2.5-6.6-5.6-6.6z"/>';
    const BADGE_COLOR = {
      qr_requests: "info",
      kitchen_ready: "success",
      booked: "brand",
      deadline_warning: "warning",
      no_show: "error",
      held: "muted"
    };
    const BADGE_BLINKS = /* @__PURE__ */ new Set(["qr_requests", "deadline_warning"]);
    function isAlt(badge) {
      return badge.kind === "qr_requests" && (badge.count ?? 0) > 1;
    }
    const toneHex = computed(() => swatchToneHex(props.config.color ?? ""));
    const isNeutral = computed(() => toneHex.value === NEUTRAL_HEX);
    const toneVar = computed(() => {
      if (isNeutral.value) return "var(--app-table-neutral-bg)";
      const tone = toneForHex(toneHex.value);
      return tone ? `var(--pos-tone-${tone}, ${toneHex.value})` : toneHex.value;
    });
    const isRound = computed(() => props.config.shape === "round");
    const faceVariant = computed(() => {
      const occ = props.state.occupancy;
      if (occ === "free") return "tf--tint";
      if (occ === "seated") return "tf--seated";
      return "tf--solid";
    });
    const chip = computed(() => {
      if (!props.state.showChip) return null;
      if (props.state.occupancy === "seated") return { kind: "seated" };
      if (props.state.occupancy !== "occupied") return null;
      if (!props.state.owner) return { kind: "other", text: "?" };
      return {
        kind: props.state.owner.kind === "me" ? "mine" : "other",
        text: props.state.owner.initials
      };
    });
    const blinkDelay = typeof performance !== "undefined" ? `-${(performance.now() / 1e3 % 3.2).toFixed(3)}s` : "0s";
    const faceRef = ref(null);
    const nameRef = ref(null);
    const amountRef = ref(null);
    const overflowing = ref(false);
    function measure() {
      const face = faceRef.value;
      if (!face) return;
      const wider = (el) => !!el && el.scrollWidth > face.clientWidth;
      overflowing.value = wider(nameRef.value) || wider(amountRef.value);
    }
    let sharedObserver = null;
    const observedCallbacks = /* @__PURE__ */ new WeakMap();
    function getSharedObserver() {
      if (typeof ResizeObserver === "undefined") return null;
      if (!sharedObserver) {
        sharedObserver = new ResizeObserver((entries) => {
          const ran = /* @__PURE__ */ new Set();
          for (const entry of entries) {
            const cb = observedCallbacks.get(entry.target);
            if (cb && !ran.has(cb)) {
              ran.add(cb);
              cb();
            }
          }
        });
      }
      return sharedObserver;
    }
    function observeEl(el) {
      var _a;
      if (!el) return;
      observedCallbacks.set(el, measure);
      (_a = getSharedObserver()) == null ? void 0 : _a.observe(el);
    }
    function unobserveEl(el) {
      var _a;
      if (!el) return;
      observedCallbacks.delete(el);
      (_a = getSharedObserver()) == null ? void 0 : _a.unobserve(el);
    }
    watch(amountRef, (el, old) => {
      if (old) unobserveEl(old);
      if (el) {
        observeEl(el);
        measure();
      }
    });
    onMounted(() => {
      observeEl(faceRef.value);
      observeEl(nameRef.value);
      measure();
    });
    onBeforeUnmount(() => {
      unobserveEl(faceRef.value);
      unobserveEl(nameRef.value);
      unobserveEl(amountRef.value);
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "faceRef",
        ref: faceRef,
        class: normalizeClass(["table-face", [
          faceVariant.value,
          {
            "tf--neutral": isNeutral.value,
            "is-round": isRound.value,
            "is-ringed": __props.state.ring,
            "is-dimmed": __props.state.dimmed,
            "tf--of": overflowing.value
          }
        ]]),
        style: normalizeStyle({ "--tf-tone": toneVar.value, "--tf-blink-delay": unref(blinkDelay) })
      }, [
        createElementVNode("span", {
          ref_key: "nameRef",
          ref: nameRef,
          class: "tf-name"
        }, toDisplayString(__props.config.name), 513),
        __props.state.showPrice && __props.price ? (openBlock(), createElementBlock("span", {
          key: 0,
          ref_key: "amountRef",
          ref: amountRef,
          class: "tf-amount"
        }, toDisplayString(__props.price), 513)) : createCommentVNode("", true),
        chip.value ? (openBlock(), createElementBlock("span", {
          key: 1,
          class: normalizeClass(["tf-chip", `tf-chip--${chip.value.kind}`]),
          "aria-label": chip.value.kind === "seated" ? __props.seatedLabel || void 0 : void 0
        }, [
          chip.value.kind === "seated" ? (openBlock(), createElementBlock("svg", {
            key: 0,
            viewBox: "0 0 24 24",
            fill: "currentColor",
            "aria-hidden": "true",
            innerHTML: USERS_SOLID
          })) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
            createTextVNode(toDisplayString(chip.value.text), 1)
          ], 64))
        ], 10, _hoisted_1)) : createCommentVNode("", true),
        __props.state.badges.length ? (openBlock(), createElementBlock("span", _hoisted_2, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(__props.state.badges, (badge) => {
            return openBlock(), createElementBlock("span", {
              key: badge.kind,
              class: normalizeClass(["tf-badge", [
                `tf-badge--${BADGE_COLOR[badge.kind]}`,
                {
                  "tf-badge--blink": unref(BADGE_BLINKS).has(badge.kind) && !isAlt(badge),
                  "tf-badge--alt": isAlt(badge)
                }
              ]])
            }, [
              isAlt(badge) ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
                createElementVNode("span", _hoisted_3, [
                  (openBlock(), createElementBlock("svg", {
                    viewBox: "0 0 24 24",
                    fill: "currentColor",
                    "aria-hidden": "true",
                    innerHTML: BADGE_PATHS[badge.kind]
                  }, null, 8, _hoisted_4))
                ]),
                createElementVNode("span", _hoisted_5, toDisplayString(badge.count), 1)
              ], 64)) : (openBlock(), createElementBlock("svg", {
                key: 1,
                class: "tf-badge-icon",
                viewBox: "0 0 24 24",
                fill: "currentColor",
                "aria-hidden": "true",
                innerHTML: BADGE_PATHS[badge.kind]
              }, null, 8, _hoisted_6))
            ], 2);
          }), 128))
        ])) : createCommentVNode("", true),
        renderSlot(_ctx.$slots, "default", {}, void 0, true)
      ], 6);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index74.js.map
